import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { useNavigate, NavLink, Routes, Route } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Building2, Inbox, CalendarClock, Calendar, LogOut, Search,
  ChevronRight, Clock, CheckCircle, XCircle, RotateCcw, Phone, Mail,
  MessageSquare, Eye, FileText, Car, Sun, Moon, Settings, User, Lock, Bell
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import PortalCalendar from '../components/PortalCalendar';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Theme Context ──
const ThemeContext = createContext({ dark: true, toggle: () => {} });
const useTheme = () => useContext(ThemeContext);

function ThemeProvider({ children }) {
  const [dark, setDark] = useState(() => {
    const saved = localStorage.getItem('portal_theme');
    return saved ? saved === 'dark' : true;
  });
  const toggle = () => {
    setDark(prev => {
      localStorage.setItem('portal_theme', !prev ? 'dark' : 'light');
      return !prev;
    });
  };
  return <ThemeContext.Provider value={{ dark, toggle }}>{children}</ThemeContext.Provider>;
}

// ── Theme helpers ──
const t = (dark) => ({
  dark,
  bg: dark ? 'bg-slate-950' : 'bg-gray-50',
  card: dark ? 'bg-[#0A0A0A] border-[#262626]' : 'bg-white border-gray-200',
  cardHover: dark ? 'hover:border-[#404040]' : 'hover:border-gray-400',
  header: dark ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200 shadow-sm',
  nav: dark ? 'bg-slate-900/50 border-slate-800' : 'bg-gray-50 border-gray-200',
  navActive: dark ? 'border-red-500 text-white' : 'border-red-500 text-gray-900',
  navInactive: dark ? 'border-transparent text-slate-400 hover:text-white' : 'border-transparent text-gray-500 hover:text-gray-900',
  text: dark ? 'text-white' : 'text-gray-900',
  textSub: dark ? 'text-slate-400' : 'text-gray-700',
  textMuted: dark ? 'text-slate-500' : 'text-gray-600',
  textTiny: dark ? 'text-slate-600' : 'text-gray-500',
  input: dark ? 'bg-slate-900/50 border-slate-800 text-white' : 'bg-white border-gray-300 text-gray-900',
  select: dark ? 'bg-slate-900/50 border-slate-800 text-slate-300' : 'bg-white border-gray-300 text-gray-700',
  expanded: dark ? 'border-[#262626]' : 'border-gray-200',
  code: dark ? 'bg-[#111] border-[#262626] text-slate-300' : 'bg-gray-100 border-gray-200 text-gray-700',
  previewBg: dark ? 'bg-[#0A0A0A] border-[#262626]' : 'bg-white border-gray-200',
  badge: {
    green: dark ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-green-50 text-green-700 border-green-200',
    orange: dark ? 'bg-orange-500/10 text-orange-400 border-orange-500/20' : 'bg-orange-50 text-orange-700 border-orange-200',
    red: dark ? 'bg-red-500/10 text-red-400 border-red-500/20' : 'bg-red-50 text-red-700 border-red-200',
  },
  dot: { green: 'bg-green-500', orange: 'bg-orange-500', red: 'bg-red-500' },
  previewBtn: dark ? 'border-[#333] bg-transparent hover:bg-[#1a1a1a] text-slate-400 hover:text-white' : 'border-gray-300 bg-transparent hover:bg-gray-100 text-gray-500 hover:text-gray-900',
  empty: dark ? 'text-slate-700' : 'text-gray-500',
  emptyText: dark ? 'text-slate-500' : 'text-gray-600',
  schedAssistant: dark ? 'bg-slate-800 text-slate-200 border-slate-700/50' : 'bg-gray-100 text-gray-800 border-gray-200',
  schedCustomer: dark ? 'bg-red-600/20 text-red-200 border-red-700/30' : 'bg-red-50 text-red-800 border-red-200',
});

const STATUS_COLORS = {
  sent_via_crm: 'bg-emerald-500/20 text-emerald-400',
  pending_crm: 'bg-yellow-500/20 text-yellow-400',
  sending_crm: 'bg-blue-500/20 text-blue-400',
  crm_send_failed: 'bg-red-500/20 text-red-400',
  call_logged: 'bg-emerald-500/20 text-emerald-400',
  pending_call_log: 'bg-yellow-500/20 text-yellow-400',
  skipped_bad_lead: 'bg-slate-500/20 text-slate-400',
  failed_no_email: 'bg-orange-500/20 text-orange-400',
};

const SCHED_STATUS = {
  awaiting_type: { label: 'Awaiting Choice', color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
  awaiting_visit_reason: { label: 'Awaiting Reason', color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
  awaiting_datetime: { label: 'Awaiting Date', color: 'bg-blue-500/20 text-blue-400', icon: Clock },
  awaiting_contact: { label: 'Awaiting Contact', color: 'bg-blue-500/20 text-blue-400', icon: Clock },
  confirmed: { label: 'Confirmed', color: 'bg-emerald-500/20 text-emerald-400', icon: CheckCircle },
  rescheduling: { label: 'Rescheduling', color: 'bg-orange-500/20 text-orange-400', icon: RotateCcw },
  cancelled: { label: 'Cancelled', color: 'bg-red-500/20 text-red-400', icon: XCircle },
};

function usePortalAuth() {
  const token = localStorage.getItem('portal_token');
  const user = JSON.parse(localStorage.getItem('portal_user') || 'null');
  const headers = { Authorization: `Bearer ${token}` };
  const setUser = (u) => {
    localStorage.setItem('portal_user', JSON.stringify(u));
    window.dispatchEvent(new Event('portal_user_updated'));
  };
  return { token, user, headers, setUser };
}

function PortalLeads() {
  const { headers, user } = usePortalAuth();
  const { dark } = useTheme();
  const th = t(dark);
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [previewLead, setPreviewLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dealerships, setDealerships] = useState([]);
  const [dealershipFilter, setDealershipFilter] = useState('');
  const isMaster = user?.role === 'master';

  useEffect(() => {
    if (isMaster) {
      axios.get(`${API}/api/portal/dealerships`, { headers })
        .then(({ data }) => setDealerships(data.dealerships || []))
        .catch(() => {});
    }
  }, [isMaster]);

  const [total, setTotal] = useState(0);

  const fetchLeads = useCallback(async (silent = false) => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (dealershipFilter) params.append('dealership_id', dealershipFilter);
      const { data } = await axios.get(`${API}/api/portal/leads?${params}`, { headers });
      setLeads(data.leads || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [search, dealershipFilter]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  // Only refresh when new leads arrive (check count silently every 10s)
  useEffect(() => {
    let lastTotal = total;
    const interval = setInterval(async () => {
      try {
        const params = new URLSearchParams();
        if (search) params.append('search', search);
        if (dealershipFilter) params.append('dealership_id', dealershipFilter);
        params.append('limit', '1');
        const { data } = await axios.get(`${API}/api/portal/leads?${params}`, { headers });
        const newTotal = data.total || 0;
        if (newTotal !== lastTotal) {
          lastTotal = newTotal;
          fetchLeads(true);
        }
      } catch (err) { console.error('Portal leads refresh error:', err); }
    }, 10000);
    return () => clearInterval(interval);
  }, [search, dealershipFilter, total]);

  return (
    <div className="space-y-4" data-testid="portal-leads">
      <h2 className={`text-lg font-bold ${th.text}`}>Leads</h2>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${th.textMuted}`} />
          <Input
            placeholder="Search by name, email, phone..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className={`pl-8 ${th.input} text-xs h-8`}
            data-testid="portal-leads-search"
          />
        </div>
        {isMaster && (
          <select
            value={dealershipFilter}
            onChange={e => setDealershipFilter(e.target.value)}
            className={`${th.select} text-xs rounded-md h-8 px-2`}
            data-testid="portal-dealer-filter"
          >
            <option value="">All Dealerships</option>
            {dealerships.map(d => (
              <option key={d.dealership_id} value={d.dealership_id}>{d.name}</option>
            ))}
          </select>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : leads.length === 0 ? (
        <p className="text-slate-500 text-sm text-center py-8">No leads found</p>
      ) : (
        <div className="space-y-2" data-testid="portal-leads-list">
          {leads.map((lead, i) => (
            <div
              key={lead.lead_id}
              onClick={() => setSelectedLead(selectedLead?.lead_id === lead.lead_id ? null : lead)}
              className={`${th.card} border rounded-lg p-4 ${th.cardHover} cursor-pointer transition-all`}
              data-testid={`portal-lead-${lead.lead_id}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                  lead.response_status === 'sent' || lead.response_status === 'sent_via_crm' || lead.response_status === 'call_logged' ? th.dot.green :
                  lead.response_status === 'pending_review' || lead.response_status === 'pending_crm' || lead.response_status === 'pending_call_log' ? th.dot.orange : th.dot.red
                }`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-sm font-medium ${th.text}`}>{lead.customer_name || 'Unknown'}</span>
                    <Badge className={`text-[9px] font-bold tracking-wider uppercase border ${
                      lead.response_status === 'sent' || lead.response_status === 'sent_via_crm' || lead.response_status === 'call_logged'
                        ? th.badge.green
                        : lead.response_status === 'pending_review' || lead.response_status === 'pending_crm' || lead.response_status === 'pending_call_log'
                        ? th.badge.orange
                        : th.badge.red
                    }`}>
                      {(lead.response_status || '').replace(/_/g, ' ')}
                    </Badge>
                  </div>
                  <div className={`flex items-center gap-3 mt-1 text-xs ${th.textMuted}`}>
                    <span className="flex items-center gap-1">
                      <Building2 className="w-3 h-3" /> {lead.dealership_name}
                    </span>
                    {lead.vehicle_make && (
                      <span className="flex items-center gap-1">
                        <Car className="w-3 h-3" />
                        {[lead.vehicle_year, lead.vehicle_make, lead.vehicle_model].filter(Boolean).join(' ')}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Mail className="w-3 h-3" /> {lead.customer_email || lead.customer_phone || '—'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {lead.response_body && (
                    <button
                      className={`p-1.5 rounded border ${th.previewBtn} transition-colors`}
                      title="Preview Response"
                      data-testid={`portal-preview-${lead.lead_id}`}
                      onClick={(e) => { e.stopPropagation(); setPreviewLead(lead); }}
                    >
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <div className="text-right">
                    <span className={`text-[10px] ${th.textMuted}`}>
                      {lead.received_at ? new Date(lead.received_at).toLocaleString() : ''}
                    </span>
                  </div>
                </div>
              </div>

              {selectedLead?.lead_id === lead.lead_id && (
                <div className={`mt-3 pt-3 border-t ${th.expanded} space-y-3`}>
                  <div>
                    <h4 className={`text-[10px] ${th.textMuted} uppercase tracking-wider mb-1 font-bold`}>Raw Lead Notification</h4>
                    <div className={`${th.code} border rounded p-3 text-xs max-h-48 overflow-y-auto whitespace-pre-wrap font-mono`}>
                      {lead.email_body || 'No raw email body available'}
                    </div>
                  </div>
                  <div className={`flex gap-4 text-xs ${th.textSub}`}>
                    {lead.customer_phone && <span><Phone className="w-3 h-3 inline mr-1" />{lead.customer_phone}</span>}
                    {lead.customer_email && <span><Mail className="w-3 h-3 inline mr-1" />{lead.customer_email}</span>}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Response Preview Modal */}
      {previewLead && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setPreviewLead(null)}>
          <div className={`${th.previewBg} border rounded-lg w-full max-w-[900px] max-h-[85vh] overflow-y-auto`} onClick={e => e.stopPropagation()} data-testid="portal-preview-modal">
            <div className={`p-4 border-b ${th.expanded}`}>
              <div className="flex items-center justify-between">
                <h3 className={`${th.text} font-bold text-sm uppercase tracking-wider`}>Response Preview</h3>
                <button onClick={() => setPreviewLead(null)} className={`${th.textSub} hover:${th.text} text-sm`}>Close</button>
              </div>
              <div className={`flex items-center gap-3 mt-2 text-xs ${th.textSub}`}>
                <span>To: {previewLead.customer_name}</span>
                <span className={th.textTiny}>|</span>
                <span>Vehicle: {[previewLead.vehicle_year, previewLead.vehicle_make, previewLead.vehicle_model].filter(Boolean).join(' ') || '—'}</span>
              </div>
              {previewLead.response_subject && (
                <div className={`mt-2 p-2 ${th.code} border rounded text-xs ${th.text}`}>
                  Subject: {previewLead.response_subject}
                </div>
              )}
            </div>
            <div className="p-4">
              <div className="bg-white rounded-lg overflow-x-auto">
                <div
                  className="text-sm text-gray-800 leading-relaxed p-6 [&_img]:max-w-full [&_img]:h-auto"
                  style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                  dangerouslySetInnerHTML={{ __html: previewLead.response_body }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PortalScheduling() {
  const { headers } = usePortalAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchConversations = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/portal/scheduling`, { headers });
      setConversations(data.conversations || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  return (
    <div className="space-y-4" data-testid="portal-scheduling">
      <h2 className="text-lg font-bold text-white">Conversations</h2>

      {loading ? (
        <p className="text-slate-500 text-sm text-center py-8">Loading...</p>
      ) : conversations.length === 0 ? (
        <div className="text-center py-8">
          <CalendarClock className="w-8 h-8 text-slate-700 mx-auto mb-2" />
          <p className="text-slate-500 text-sm">No scheduling conversations yet</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {conversations.map(conv => {
            const st = SCHED_STATUS[conv.status] || SCHED_STATUS.awaiting_type;
            const StIcon = st.icon;
            return (
              <div
                key={conv.conversation_id}
                className="bg-slate-900/50 border border-slate-800 rounded-lg p-3 hover:border-slate-700 cursor-pointer transition-colors"
                onClick={() => setSelectedConv(selectedConv?.conversation_id === conv.conversation_id ? null : conv)}
                data-testid={`portal-sched-${conv.conversation_id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-white text-sm font-medium">{conv.customer_name}</span>
                    <span className="text-slate-500 text-xs ml-2">{conv.vehicle}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {conv.channel === 'sms' ? <Phone className="w-3 h-3 text-slate-500" /> : <Mail className="w-3 h-3 text-slate-500" />}
                    <Badge className={`${st.color} text-[10px]`}>
                      <StIcon className="w-3 h-3 mr-1" />{st.label}
                    </Badge>
                  </div>
                </div>
                {conv.appointment_display && (
                  <p className="text-xs text-slate-400 mt-0.5">
                    {conv.appointment_type === 'test_drive' ? 'Test Drive' : 'Dealer Visit'} — {conv.appointment_display}
                  </p>
                )}

                {selectedConv?.conversation_id === conv.conversation_id && (
                  <div className="mt-3 pt-3 border-t border-slate-800 space-y-2">
                    {(conv.messages || []).map((msg, i) => (
                      <div key={i} className={`flex ${msg.role === 'assistant' ? 'justify-start' : 'justify-end'}`}>
                        <div className={`max-w-[80%] p-2 rounded-lg text-xs ${
                          msg.role === 'assistant'
                            ? 'bg-slate-800 text-slate-200'
                            : 'bg-red-600/20 text-red-200'
                        }`}>
                          <p className="whitespace-pre-wrap">{msg.content}</p>
                          <p className="text-[9px] text-slate-500 mt-1">{new Date(msg.timestamp).toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PortalAppointments() {
  const { headers, user } = usePortalAuth();
  const { dark } = useTheme();
  const th = t(dark);
  const [appointments, setAppointments] = useState([]);
  const [businessHours, setBusinessHours] = useState({});
  const [dealerTimezone, setDealerTimezone] = useState('');
  const [loading, setLoading] = useState(true);
  const [dealerships, setDealerships] = useState([]);
  const [dealershipFilter, setDealershipFilter] = useState('');
  const isMaster = user?.role === 'master';

  useEffect(() => {
    if (isMaster) {
      axios.get(`${API}/api/portal/dealerships`, { headers })
        .then(({ data }) => setDealerships(data.dealerships || []))
        .catch(() => {});
    }
  }, [isMaster]);

  const fetchAppointments = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (dealershipFilter) params.append('dealership_id', dealershipFilter);
      const { data } = await axios.get(`${API}/api/portal/appointments?${params}`, { headers });
      setAppointments(data.appointments || []);
      if (data.business_hours) setBusinessHours(data.business_hours);
      if (data.timezone) setDealerTimezone(data.timezone);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [dealershipFilter]);

  useEffect(() => { fetchAppointments(); }, [fetchAppointments]);

  const handleUpdateAppointment = async (conversationId, updates) => {
    try {
      await axios.put(`${API}/api/portal/appointments/${conversationId}`, updates, { headers });
      await fetchAppointments();
      toast.success('Appointment updated');
    } catch (err) {
      toast.error('Failed to update appointment');
      console.error(err);
    }
  };

  if (loading) return <p className={`${th.textSub} text-sm text-center py-8`}>Loading...</p>;

  return (
    <div data-testid="portal-appointments">
      <PortalCalendar
        appointments={appointments}
        onUpdateAppointment={handleUpdateAppointment}
        theme={th}
        businessHours={businessHours}
        timezone={dealerTimezone}
        dealerships={isMaster ? dealerships : []}
        dealershipFilter={dealershipFilter}
        onDealershipFilterChange={setDealershipFilter}
      />
    </div>
  );
}

export default function PortalDashboard() {
  return (
    <ThemeProvider>
      <PortalDashboardInner />
    </ThemeProvider>
  );
}

function PortalSettings() {
  const { headers, user, setUser } = usePortalAuth();
  const { dark } = useTheme();
  const th = t(dark);

  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [notifyEmail, setNotifyEmail] = useState(user?.notify_email !== false);
  const [notifySms, setNotifySms] = useState(user?.notify_sms || false);
  const [saving, setSaving] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPw, setChangingPw] = useState(false);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/api/portal/profile`, {
        name, email, phone,
        notify_email: notifyEmail,
        notify_sms: notifySms,
      }, { headers });
      if (data.user) {
        const updated = { ...user, ...data.user };
        setUser(updated);
        localStorage.setItem('portal_user', JSON.stringify(updated));
      }
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally { setSaving(false); }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    setChangingPw(true);
    try {
      await axios.put(`${API}/api/portal/password`, {
        current_password: currentPassword,
        new_password: newPassword,
      }, { headers });
      toast.success('Password updated');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally { setChangingPw(false); }
  };

  const inputCls = `text-sm ${dark ? 'bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500' : 'bg-white border-gray-300 text-gray-900 placeholder:text-gray-400'}`;
  const labelCls = `text-xs font-medium block mb-1 ${th.textSub}`;
  const cardCls = `rounded-lg border p-5 ${th.card}`;

  return (
    <div className="space-y-6" data-testid="portal-settings">
      {/* Profile Section */}
      <div className={cardCls}>
        <div className="flex items-center gap-2 mb-4">
          <User className={`w-4 h-4 ${th.textMuted}`} />
          <h2 className={`text-sm font-semibold ${th.text}`}>Profile</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Full Name</label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" className={inputCls} data-testid="settings-name" />
          </div>
          <div>
            <label className={labelCls}>Email Address</label>
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" className={inputCls} data-testid="settings-email" />
          </div>
          <div>
            <label className={labelCls}>Phone Number</label>
            <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1XXXXXXXXXX" className={inputCls} data-testid="settings-phone" />
          </div>
          <div>
            <label className={labelCls}>Dealership</label>
            <Input value={user?.dealership_name || ''} disabled className={`text-sm ${dark ? 'bg-slate-900 border-slate-700 text-slate-400' : 'bg-gray-100 border-gray-300 text-gray-500'}`} />
          </div>
        </div>
        <div className="mt-4">
          <Button onClick={handleSaveProfile} disabled={saving} className="bg-red-600 hover:bg-red-700 text-white text-xs" data-testid="settings-save-profile">
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Notification Preferences */}
      <div className={cardCls}>
        <div className="flex items-center gap-2 mb-4">
          <Bell className={`w-4 h-4 ${th.textMuted}`} />
          <h2 className={`text-sm font-semibold ${th.text}`}>Notification Preferences</h2>
        </div>
        <div className="space-y-3">
          <label className={`flex items-center gap-3 cursor-pointer`}>
            <input
              type="checkbox"
              checked={notifyEmail}
              onChange={e => setNotifyEmail(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
              data-testid="settings-notify-email"
            />
            <div>
              <span className={`text-sm ${th.text}`}>Email Notifications</span>
              <p className={`text-xs ${th.textMuted}`}>Receive lead alerts and appointment updates via email</p>
            </div>
          </label>
          <label className={`flex items-center gap-3 cursor-pointer`}>
            <input
              type="checkbox"
              checked={notifySms}
              onChange={e => setNotifySms(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
              data-testid="settings-notify-sms"
            />
            <div>
              <span className={`text-sm ${th.text}`}>SMS Notifications</span>
              <p className={`text-xs ${th.textMuted}`}>Receive lead alerts and appointment updates via text message</p>
            </div>
          </label>
        </div>
        <div className="mt-4">
          <Button onClick={handleSaveProfile} disabled={saving} className="bg-red-600 hover:bg-red-700 text-white text-xs" data-testid="settings-save-notifications">
            {saving ? 'Saving...' : 'Save Preferences'}
          </Button>
        </div>
      </div>

      {/* Change Password */}
      <div className={cardCls}>
        <div className="flex items-center gap-2 mb-4">
          <Lock className={`w-4 h-4 ${th.textMuted}`} />
          <h2 className={`text-sm font-semibold ${th.text}`}>Change Password</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>Current Password</label>
            <Input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} placeholder="Enter current password" className={inputCls} data-testid="settings-current-pw" />
          </div>
          <div>
            <label className={labelCls}>New Password</label>
            <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Enter new password" className={inputCls} data-testid="settings-new-pw" />
          </div>
          <div>
            <label className={labelCls}>Confirm New Password</label>
            <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirm new password" className={inputCls} data-testid="settings-confirm-pw" />
          </div>
        </div>
        <div className="mt-4">
          <Button onClick={handleChangePassword} disabled={changingPw || !currentPassword || !newPassword || !confirmPassword} className="bg-red-600 hover:bg-red-700 text-white text-xs" data-testid="settings-change-pw">
            {changingPw ? 'Updating...' : 'Change Password'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function PortalDashboardInner() {
  const navigate = useNavigate();
  const { token, user } = usePortalAuth();
  const { dark, toggle } = useTheme();
  const th = t(dark);

  useEffect(() => {
    if (!token || !user) {
      navigate('/portal/login');
    }
  }, [token, user, navigate]);

  const handleLogout = () => {
    localStorage.removeItem('portal_token');
    localStorage.removeItem('portal_user');
    navigate('/portal/login');
  };

  if (!user) return null;

  const navItems = [
    { path: '/portal', label: 'Leads', icon: Inbox, end: true },
    { path: '/portal/scheduling', label: 'Conversations', icon: CalendarClock },
    { path: '/portal/appointments', label: 'Appointments', icon: Calendar },
    { path: '/portal/settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className={`min-h-screen ${th.bg}`} data-testid="portal-dashboard">
      <header className={`${th.header} border-b px-6 py-3 flex items-center justify-between`}>
        <div className="flex items-center gap-3">
          <Building2 className="w-5 h-5 text-red-500" />
          <div>
            <h1 className={`${th.text} text-sm font-bold`}>{user.dealership_name}</h1>
            <p className={`${th.textMuted} text-[10px]`}>ETA Dealer Portal</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            className={`p-1.5 rounded-md border ${dark ? 'border-slate-700 hover:bg-slate-800' : 'border-gray-300 hover:bg-gray-100'} transition-colors`}
            title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
            data-testid="theme-toggle"
          >
            {dark ? <Sun className="w-4 h-4 text-yellow-400" /> : <Moon className="w-4 h-4 text-gray-600" />}
          </button>
          <span className={`${th.textSub} text-xs`}>{user.name}</span>
          <Button variant="ghost" size="sm" onClick={handleLogout} className={`${th.textSub} hover:${th.text} h-7 text-xs`}>
            <LogOut className="w-3.5 h-3.5 mr-1" />Sign Out
          </Button>
        </div>
      </header>

      <nav className={`${th.nav} border-b px-6`}>
        <div className="flex gap-1">
          {navItems.map(item => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.end}
                className={({ isActive }) =>
                  `px-4 py-2.5 text-xs font-medium flex items-center gap-1.5 border-b-2 transition-colors ${
                    isActive ? th.navActive : th.navInactive
                  }`
                }
                data-testid={`portal-nav-${item.label.toLowerCase()}`}
              >
                <Icon className="w-3.5 h-3.5" />{item.label}
              </NavLink>
            );
          })}
        </div>
      </nav>

      <Routes>
        <Route path="appointments" element={<PortalAppointments />} />
        <Route path="*" element={
          <main className="max-w-5xl mx-auto p-6">
            <Routes>
              <Route index element={<PortalLeads />} />
              <Route path="scheduling" element={<PortalScheduling />} />
              <Route path="settings" element={<PortalSettings />} />
            </Routes>
          </main>
        } />
      </Routes>
    </div>
  );
}
